import ExportButton from "@components/ExportButton";
import GroupInfoBadge from "@components/GroupInfoBadge";
import { PageLayoutWithHelp } from "@features/page-help";
import type { NetworkTelemetryRow } from "@features/reports/api";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { useObjectStore } from "@shared/store/objectStore";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Space, Tabs } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";

import NetworkTelemetryChart from "./NetworkTelemetryChart";
import NetworkTelemetryFilters from "./NetworkTelemetryFilters";
import { getNetworkTelemetryColumns } from "./networkTelemetryColumns";
import { useNetworkTelemetryStore } from "./store";
import { useNetworkTelemetryReport } from "./useNetworkTelemetryReport";

const ReportsNetworkTelemetryPage = () => {
  const selectedObject = useObjectStore((s) => s.selectedObject);
  const {
    scope,
    metric,
    coefficient,
    interval,
    selectedChannel,
    selectedGroup,
    selectedGroupChannel,
  } = useNetworkTelemetryStore();

  const report = useNetworkTelemetryReport();
  const {
    objectsQuery,
    objects,
    channelOptions,
    channelsQuery,
    groupOptions,
    groupsQuery,
    groupChannelOptions,
    selectedGroupData,
    ready,
    telemetryQuery,
    rows,
    exportUrl,
    currentObject,
    fromISO,
    toISO,
  } = report;

  const pagination = useStandardPagination(rows.length);
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });

  const columns = useMemo(() => getNetworkTelemetryColumns(coefficient), [coefficient]);

  const handleSendMail = useCallback(() => {
    if (!ready) return;
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : selectedObject?.objectId
        ? `Объект №${selectedObject.objectId}`
        : "Объект";
    const range = `${dayjs(fromISO).format("DD.MM.YYYY")} — ${dayjs(toISO).format("DD.MM.YYYY")}`;
    const groupLabel =
      groupOptions.find((opt) => opt.value === selectedGroup)?.label ?? selectedGroup;
    const channelLabel =
      channelOptions.find((opt) => opt.value === selectedChannel)?.label ?? selectedChannel;
    const groupChannelLabel =
      groupChannelOptions.find((opt) => opt.value === selectedGroupChannel)?.label ??
      selectedGroupChannel ??
      "";
    const targetLabel =
      scope === "group" && selectedGroupChannel
        ? `Канал ${groupChannelLabel} из группы: ${groupLabel}`
        : scope === "group"
          ? `Группа: ${groupLabel}`
          : scope === "channel"
            ? `Канал: ${channelLabel}`
            : "Все каналы объекта";
    openComposer({
      subject: `Телеметрия сети — ${objectLabel}`,
      body: `Отчёт: Телеметрия сети (мгновенная мощность)\nОбъект: ${objectLabel}\n${targetLabel}\nПериод: ${range}\nИнтервал: ${interval} мин\n\nВо вложении — XLSX.`,
      filename: `networktelemetry_${scope}_${dayjs(fromISO).format("YYYYMMDD")}.xlsx`,
    });
  }, [
    ready,
    currentObject,
    selectedObject?.objectId,
    fromISO,
    toISO,
    scope,
    selectedGroup,
    selectedChannel,
    selectedGroupChannel,
    interval,
    openComposer,
    groupOptions,
    channelOptions,
    groupChannelOptions,
  ]);

  const errorMessage = telemetryQuery.isError ? extractErrorMessage(telemetryQuery.error) : "";
  const groupMeta = telemetryQuery.data?.meta;
  const showGroupBadge =
    scope === "group" && !selectedGroupChannel && groupMeta && selectedGroupData;

  return (
    <PageLayoutWithHelp
      title="Телеметрия сети"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <NetworkTelemetryFilters
          objects={objects}
          objectsLoading={objectsQuery.isLoading}
          channelsLoading={channelsQuery.isLoading}
          channelOptions={channelOptions}
          groupsLoading={groupsQuery.isLoading}
          groupOptions={groupOptions}
          groupChannelOptions={groupChannelOptions}
          ready={ready}
          loading={telemetryQuery.isFetching}
          onReload={() => telemetryQuery.refetch()}
        />

        {!ready && (
          <Alert
            type="info"
            showIcon
            message="Выберите параметры отчёта."
            style={{ maxWidth: 600 }}
          />
        )}

        {ready && telemetryQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Не удалось загрузить данные"
            description={errorMessage}
            style={{ maxWidth: 900 }}
          />
        )}

        {ready && !telemetryQuery.isError && (
          <Tabs
            defaultActiveKey="chart"
            items={[
              {
                key: "chart",
                label: "График",
                children: (
                  <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
                    {showGroupBadge && (
                      <GroupInfoBadge
                        name={selectedGroupData.name}
                        channelsProcessed={groupMeta.channels_processed}
                        channelsTotal={groupMeta.channels_total}
                        warning={groupMeta.warning ?? undefined}
                      />
                    )}
                    <NetworkTelemetryChart
                      rows={rows}
                      metric={metric}
                      coefficient={coefficient}
                      meta={groupMeta}
                    />
                  </Space>
                ),
              },
              {
                key: "table",
                label: "Таблица",
                children: (
                  <EnterpriseTable<NetworkTelemetryRow>
                    rowKey={(row) => row.timestamp}
                    columns={columns}
                    dataSource={rows}
                    loading={telemetryQuery.isLoading}
                    pagination={pagination}
                    scroll={{ x: 900 }}
                    size="small"
                  />
                ),
              },
            ]}
          />
        )}

        {ready && !telemetryQuery.isLoading && !telemetryQuery.isError && rows.length === 0 && (
          <Alert
            type="warning"
            showIcon
            message="Нет данных"
            description="Для выбранных параметров нет записей в cnt.test_seti. Попробуйте другой период или канал."
            style={{ maxWidth: 800 }}
          />
        )}

        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsNetworkTelemetryPage;
